import { Component } from "react";
import * as Sentry from "@sentry/react";
import { AlertTriangle, RefreshCcw, Home } from "lucide-react";
import { reportError, SENTRY_ENABLED } from "@/instrument";

/**
 * Royal Lady — global crash boundary.
 * Renders a premium, on-brand fallback + safe-retry button.
 * Never leaks stack traces to the user; the technical detail is only the
 * event_id (Sentry) so support can look up the crash without asking the
 * customer for anything sensitive.
 */
class RoyalErrorBoundaryImpl extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, eventId: null, errorMsg: "" };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    let eventId = null;
    try {
      if (SENTRY_ENABLED) {
        Sentry.withScope((scope) => {
          scope.setTag("module", "react-boundary");
          scope.setExtras({ componentStack: errorInfo?.componentStack || "" });
          eventId = Sentry.captureException(error);
        });
      } else {
        reportError(error, { module: "react-boundary" });
      }
    } catch (_e) { /* swallow */ }
    this.setState({ eventId, errorMsg: String(error?.message || "") });
  }

  reset = () => {
    this.setState({ hasError: false, eventId: null, errorMsg: "" });
  };

  goHome = () => {
    try { window.location.assign("/"); }
    catch (_e) { /* SSR safety */ }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <main
        data-testid="app-error-fallback"
        className="relative min-h-screen bg-black text-white"
      >
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(closest-side_at_50%_35%,rgba(212,175,55,0.28),transparent_70%)]" />
        <div className="relative mx-auto flex min-h-screen max-w-lg flex-col items-center justify-center px-6 text-center">
          <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 text-[#D4AF37]">
            <AlertTriangle className="h-6 w-6" />
          </div>
          <span className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]">Royal Lady</span>
          <h1 className="mt-2 font-heading text-3xl sm:text-4xl">
            A little wrinkle in the silk
          </h1>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-white/70">
            Something didn't render as it should have. We've been quietly notified — no personal
            details were sent. Please try again, or return to the storefront.
          </p>

          {this.state.eventId && (
            <div
              data-testid="app-error-id"
              className="mt-5 border border-[#D4AF37]/30 bg-white/5 px-3 py-1 font-mono text-[10px] tracking-widest text-[#D4AF37]"
            >
              Ref: {String(this.state.eventId).slice(0, 12)}
            </div>
          )}

          <div className="mt-8 flex w-full max-w-xs flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={this.reset}
              data-testid="app-error-retry"
              className="inline-flex flex-1 items-center justify-center gap-2 bg-[#D4AF37] px-4 py-3 text-[11px] font-semibold uppercase tracking-widest text-black hover:bg-white"
            >
              <RefreshCcw className="h-3.5 w-3.5" /> Try again
            </button>
            <button
              type="button"
              onClick={this.goHome}
              data-testid="app-error-home"
              className="inline-flex flex-1 items-center justify-center gap-2 border border-white/40 px-4 py-3 text-[11px] font-semibold uppercase tracking-widest text-white hover:border-[#D4AF37] hover:text-[#D4AF37]"
            >
              <Home className="h-3.5 w-3.5" /> Home
            </button>
          </div>
        </div>
      </main>
    );
  }
}

export default RoyalErrorBoundaryImpl;
